import { memo, useEffect, useMemo, useState, useRef } from 'react';
import { Image } from "@shuwen/wenlv-web-activity-library";
import styles from './index.module.scss';
import _ from 'lodash';
import { useStore } from '@/store';
import { getAssetUrl } from '@/utils/common';
import { useSpring, animated, useTransition } from 'react-spring';
import BackgroundAnimate from '@/components/BackgroundAnimate';
import { useQueryPoetry } from '@/services/hooks';
import { dynastyImages } from './instance';
import { LandmarkPoetryPlaceDetailType } from '@/store/types';
import { driveDigitalHumanBySpeak, driveDigitalHumanByText } from '@/pages/DigitalHuman/util/tts-manage';
import { getScenicDataInsights } from '@/services/get';


const LandmarkPoetryDetails = () => {
  const curIndexRef = useRef(0);
  const [authorList, setAuthorList] = useState<any>([]);
  const placeId = useStore((state) => state.landmarkPoetryPlaceId);
  const placeType = useStore((state) => state.landmarkPoetryPlaceType);
  const placeAuthor = useStore((state) => state.landmarkPoetryPlaceAuthor);
  const setLandmarkPoetryMode = useStore((state) => state.setLandmarkPoetryMode);
  const { data } = useQueryPoetry();

  const { place, targetList } = useMemo(() => {
    const place = data?.find((item: any) => item.entity_id === placeId);
    const authors: any = {};
    const placelist = place?.ext_neighborList.filter((item: any) => item.vertex.label === "creative_work" && item.vertex.author);
    const authorIamges = place?.ext_neighborList.filter((item: any) => item.vertex.label === "ppl");
    placelist?.forEach((item: any) => {
      const authorName = item.vertex?.author?.[0] || '';
      if (!authors[authorName]) {
        authors[authorName] = [];
      }
      authors[authorName].push(item.vertex);

    });
    const authorsArray = Object.entries(authors).map(([author, works]) => ({
      author,
      works
    })).sort((a: any, b: any) => b.works.length - a.works.length);

    const authorsImages = authorsArray.map((item: any) => {
      const authorsItem: any = authorIamges?.filter((itm: any) => itm.vertex.name === item.author) || [];
      if (authorsItem?.length > 0 && authorsItem?.[0].vertex.image) {
        return {
          image: authorsItem?.[0].vertex.image,
          ...item
        }
      } else {
        const image = onChangeImage(item);
        return {
          image,
          ...item
        };
      }
    });

    return {
      place,
      targetList: _.chunk(authorsImages, 15)
    }
  }, [data, placeId]);

  // 是否应该展示
  const shouldShow = placeType === LandmarkPoetryPlaceDetailType.POET && !!placeId && !placeAuthor;
  // 是否展示了
  const isShow = placeType === LandmarkPoetryPlaceDetailType.POET && !!place && !placeAuthor;

  const scrollInterval = useRef<any>(null);
  const scrollContainer = useRef<any>(null);

  const transitions = useTransition(curIndexRef.current, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 500 },
  });

  // 语音控制
  useEffect(() => {
    if (shouldShow && !isShow) {
      console.warn('未找到相关地标', placeId);
      driveDigitalHumanByText('未找到相关地标，你可以选择其他地标');
    }
    if (!isShow) return;
    if (place) {
      const xihuInsight = getScenicDataInsights(place.biz_info?.scenic_data_insights);
      if (xihuInsight) {
        driveDigitalHumanBySpeak(xihuInsight);
      }
    }
  }, [isShow, shouldShow, place])

  useEffect(() => {
    curIndexRef.current = 0;
    setAuthorList(targetList[curIndexRef.current]);
    if (targetList.length > 1) {
      const timer = setInterval(() => {
        if (curIndexRef.current < targetList?.length - 1) {
          curIndexRef.current += 1;
        } else {
          curIndexRef.current = 0;
        }
        setAuthorList(targetList[curIndexRef.current]);
      }, 10000);

      return () => clearInterval(timer);
    }
  }, [targetList]);

  const contentAnimate = useSpring({
    opacity: isShow ? 1 : 0,
    scale: isShow ? 1 : 0,
    config: {
      duration: 500,
    },
  });


  const onAngleStep = (num: any) => {
    const radius = num < 8 ? 590 : 400;
    const numberOfItems = num < 8 ? 8 : 7;
    const angleStep = 270 / numberOfItems;
    const angle = angleStep * num - 26;
    const inAngle = angleStep * num + 26;
    const radians = (angle * Math.PI) / 180;
    const inRadians = (inAngle * Math.PI) / 180;
    const x = radius + radius * Math.cos(num < 8 ? radians : inRadians);
    const y = radius + radius * Math.sin(num < 8 ? radians : inRadians);
    if (num < 8) {
      return {
        left: x,
        bottom: y - 40,
        transition: 'opacity 0.5s',
        transform: num < 4 ? `translate(-80px, 20px) rotateY(0deg)` : `translate(-140px, 20px) rotate(${0}deg)`
      }
    } else {
      return {
        left: x,
        bottom: y,
        transition: 'opacity 0.5s',
        transform: num < 11 ? `translate(110px, -100px) rotateY(0deg)` : `translate(50px, -120px) rotate(${0}deg)`
      }
    }


  }
  useEffect(() => {
    if (scrollContainer.current) {
      startAutoScroll();
    }
    return () => {
      clearInterval(scrollInterval.current);
      scrollInterval.current = null;
    };
  }, [scrollContainer.current]);

  function startAutoScroll() {
    scrollInterval.current = setInterval(() => {
      if (!scrollContainer.current) {
        return false;
      }
      const scrollDistance = 1;
      const currentScroll = scrollContainer.current.scrollTop;
      scrollContainer.current.scrollTop = currentScroll + scrollDistance;
      if (currentScroll >= scrollContainer.current.scrollHeight - scrollContainer.current.clientHeight) {
        scrollContainer.current.scrollTop = 0;
      }
    }, 80);
  }
  const HighlightNumbers = (text: string) => {
    const parts = text?.split(/(\d+)/).filter(Boolean) || [];
    return (
      <>
        {parts.map((part, index) => (
          // 如果是数字，则用<span>包裹并应用样式
          <span key={index} style={{
            color: part.match(/^\d+$/) ? '#9D805D' : 'inherit',
            fontSize: part.match(/^\d+$/) ? '30px' : '24px',
            fontWeight: part.match(/^\d+$/) ? '800' : '400',
            textShadow: part.match(/^\d+$/) ? `-2px -2px 0 white,
            2px -2px 0 white,
            -2px 2px 0 white,
            2px 2px 0 white,
            0 0 0 white,
            0 0 0 white,
            0 0 0 white,
            0 0 0 white` : '',
          }}>
            {part}
          </span>
        ))}
      </>
    );
  }
  function onChangeImage(data: any) {
    // if (data.works[0]?.dynasty && dynastyImages[data.works[0]?.dynasty]) {
    //   const imgs = dynastyImages[data.works[0]?.dynasty];
    //   const num = Math.floor(Math.random() * 2);
    //   return imgs[num] || ''
    // }

    // return getAssetUrl('images/landmark/newman.png');
    return;
  }

  return isShow && (
    <BackgroundAnimate visible={true} opacity={1} loop={true}>
      <animated.div className={styles.content} style={contentAnimate}>
        <div className={styles.introduce}>
          {HighlightNumbers(place?.biz_info?.insights_highlights)}
        </div>
        <div className={styles.mid}>
          {
            transitions((style, index) =>
              <animated.div style={{
                ...style,
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
              }}>
                <div
                  className={styles.circle}>
                  <div className={styles.circle_container}>
                    {/* 小圆圈左右 */}
                    <div className={styles.end_circle}>
                      <div className={styles.nside}></div>
                    </div>
                    <div className={styles.end_circle1}>
                      <div className={styles.nside1}></div>
                    </div>
                    {/* 小圆圈左右  -end */}
                    <div className={styles.name_img}
                      style={{ backgroundImage: `url(${getAssetUrl("images/landmark/nameimg.png")})` }}
                    >
                      <Image src={place?.image} alt="" className={styles.temple} />
                      <p className={styles.title}>{place?.name}</p>
                    </div>
                    {
                      authorList?.map((item: any, index: number) =>
                        index < 4 || (index > 7 && index < 11) ? (
                          <div key={index} className={styles.circle_item_left}
                            style={onAngleStep(index)}
                          >
                            <div
                              className={styles.avaterImg}
                              style={{ backgroundImage: `url(${getAssetUrl('images/landmark/BorderAvatar.png')})` }}
                              onClick={() => setLandmarkPoetryMode(place.entity_id, LandmarkPoetryPlaceDetailType.POET, item.author)}
                            >
                              {
                                item.image &&(
                                  <Image
                                    src={item.image}
                                    alt=""
                                    className={styles.templeIcon}
                                    style={index > 7 ? { marginRight: 5 } : {}}
                                  />
                                )
                              }
                            </div>
                            <p className={styles.circle_title}>{item.author}</p>
                            <p className={styles.circle_number}>{item.works.length}首</p>
                          </div>
                        ) : (
                          <div key={index} className={styles.circle_item}
                            style={onAngleStep(index)}
                          >
                            <p className={styles.circle_number}>{item.works.length}首</p>
                            <p className={styles.circle_title}>{item.author}</p>
                            <div
                              className={styles.avaterImg}
                              style={{ backgroundImage: `url(${getAssetUrl('images/landmark/BorderAvatar.png')})` }}
                              onClick={() => setLandmarkPoetryMode(place.entity_id, LandmarkPoetryPlaceDetailType.POET, item.author)}
                            >
                              {
                                item.image &&(
                                  <Image
                                    src={item.image}
                                    alt=""
                                    className={styles.templeIcon}
                                    style={index > 7 ? { marginRight: 5 } : {}}
                                  />
                                )
                              }
                            </div>
                          </div>
                        )
                      )
                    }
                    <div className={styles.circle_inside}>
                      <div className={styles.nside_circle}>
                        <div className={styles.nside}></div>
                      </div>
                      <div className={styles.nside_circle1}>
                        <div className={styles.nside}></div>
                      </div>
                      <p className={styles.nside_title}>相关诗人</p>
                    </div>

                  </div>
                </div>
              </animated.div>
            )
          }
          {/* <img src={place.image} alt={place.title} /> */}
        </div>
        {/* <div className={styles.left}
        >
          <img src={getAssetUrl("images/landmark/hangzhou.svg")} alt="" />
          <h2>{place?.name}</h2>
          <div className={styles.description} ref={scrollContainer}>{place?.summary}</div>

        </div> */}
      </animated.div>
    </BackgroundAnimate>
  );
};

export default memo(LandmarkPoetryDetails); 